import { ref, computed } from 'vue';
import { useWizardStore } from '../stores/wizard';
import logger from '../utils/logger';

/**
 * Composable for managing test progress persistence and resumption
 * Handles saving progress to backend and localStorage
 */

export interface SavedTestProgress {
	testId: string;
	sessionId: string;
	currentStep: number;
	totalSteps: number;
	answeredQuestions: number;
	lastSaved: string; // ISO timestamp
	expiresAt: string; // ISO timestamp (7 days from save)
	basicInfo: {
		childName: string;
		childAge: number;
		childSchool: string;
		childSituation: string;
	};
}

const STORAGE_KEY = 'saved-test-progress';
const EXPIRY_DAYS = 7;

export function useTestProgress() {
	const store = useWizardStore();
	const isSaving = ref(false);
	const saveError = ref<string | null>(null);

	/**
	 * Check if there's a saved test in progress
	 */
	const hasSavedProgress = computed((): boolean => {
		const saved = getSavedProgress();
		if (!saved) return false;

		// Check if expired
		const expiresAt = new Date(saved.expiresAt);
		const now = new Date();
		return expiresAt > now;
	});

	/**
	 * Get saved progress from localStorage
	 */
	function getSavedProgress(): SavedTestProgress | null {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (!saved) return null;

			const progress: SavedTestProgress = JSON.parse(saved);

			// Validate expiry
			const expiresAt = new Date(progress.expiresAt);
			const now = new Date();

			if (expiresAt <= now) {
				// Expired - clear it
				clearSavedProgress();
				return null;
			}

			return progress;
		} catch (error) {
			logger.error('Error reading saved progress', undefined, error instanceof Error ? error : new Error(String(error)));
			return null;
		}
	}

	/**
	 * Save current test progress
	 */
	async function saveProgress(): Promise<boolean> {
		isSaving.value = true;
		saveError.value = null;

		try {
			// Validate we have required data
			if (!store.testId || !store.basicInfo.childName) {
				throw new Error('Keine Test-Daten zum Speichern vorhanden');
			}

			const now = new Date();
			const expiresAt = new Date(now.getTime() + EXPIRY_DAYS * 24 * 60 * 60 * 1000);

			// Get total questions count (estimate based on typical test length)
			const answeredCount = Object.keys(store.answers).length;
			const totalQuestions = 50; // Typical test has ~50 questions

			const progress: SavedTestProgress = {
				testId: store.testId,
				sessionId: '', // Session ID is managed by backend
				currentStep: answeredCount,
				totalSteps: totalQuestions,
				answeredQuestions: answeredCount,
				lastSaved: now.toISOString(),
				expiresAt: expiresAt.toISOString(),
				basicInfo: {
					childName: store.basicInfo.childName || '',
					childAge: Number(store.basicInfo.childAge) || 0,
					childSchool: store.basicInfo.childSchool || '',
					childSituation: store.basicInfo.childSituation || '',
				},
			};

			// Save to localStorage
			localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));

			// Note: Backend persistence happens automatically via test-session API
			// The test session is already saved with each answer submission

			logger.info('✅ Progress saved successfully', { progress });
			return true;
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
			saveError.value = message;
			logger.error('Error saving progress', undefined, error instanceof Error ? error : new Error(String(error)));
			return false;
		} finally {
			isSaving.value = false;
		}
	}

	/**
	 * Resume test from saved progress
	 */
	function resumeProgress(): boolean {
		try {
			const saved = getSavedProgress();
			if (!saved) {
				logger.warn('No saved progress to resume');
				return false;
			}

			// Note: The actual test data (answers) is already in the wizard store
			// from localStorage persistence. We just need to validate it matches.

			if (store.testId !== saved.testId) {
				logger.warn('Test ID mismatch - clearing saved progress');
				clearSavedProgress();
				return false;
			}

			logger.info('✅ Resuming test from saved progress', { saved });
			return true;
		} catch (error) {
			logger.error('Error resuming progress', undefined, error instanceof Error ? error : new Error(String(error)));
			return false;
		}
	}

	/**
	 * Clear saved progress
	 */
	function clearSavedProgress(): void {
		try {
			localStorage.removeItem(STORAGE_KEY);
			logger.info('🧹 Saved progress cleared');
		} catch (error) {
			logger.error('Error clearing saved progress', undefined, error instanceof Error ? error : new Error(String(error)));
		}
	}

	/**
	 * Get progress summary for display
	 */
	const progressSummary = computed(() => {
		const saved = getSavedProgress();
		if (!saved) return null;

		const lastSaved = new Date(saved.lastSaved);
		const expiresAt = new Date(saved.expiresAt);
		const now = new Date();
		const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

		return {
			childName: saved.basicInfo.childName,
			progress: `${saved.answeredQuestions} von ${saved.totalSteps} Fragen`,
			progressPercent: Math.round((saved.answeredQuestions / saved.totalSteps) * 100),
			lastSaved: lastSaved.toLocaleDateString('de-DE', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			}),
			daysUntilExpiry,
			expiresAt: expiresAt.toLocaleDateString('de-DE'),
		};
	});

	/**
	 * Auto-save progress (debounced)
	 */
	let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;

	function scheduleAutoSave(): void {
		if (autoSaveTimeout) {
			clearTimeout(autoSaveTimeout);
		}

		// Auto-save after 2 seconds of inactivity
		autoSaveTimeout = setTimeout(() => {
			saveProgress();
		}, 2000);
	}

	return {
		// State
		isSaving,
		saveError,
		hasSavedProgress,
		progressSummary,

		// Methods
		saveProgress,
		resumeProgress,
		clearSavedProgress,
		getSavedProgress,
		scheduleAutoSave,
	};
}
